// src/components/FaturaProforma.jsx

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useSearchParams } from 'react-router-dom';
import { PaymentMethodsConfig } from '../../utils/paymentMethods';
import { formatarMoeda } from '../../utils/formatters';
import api from '../../services/api';
import pagamentoService from '../../services/pagamentoService';
import pagamentoPublicoService from '../../services/pagamentoPublicoService';
import { useNotificacao } from '../../contexts/NotificacaoContext';
import ArrowBack from '@mui/icons-material/ArrowBack';

const PaymentButton = styled(Button)(({ method }) => ({
  backgroundColor: PaymentMethodsConfig[method]?.buttonColor || '#2e7d32',
  color: 'white',
  padding: '10px 20px',
  width: '100%',
  maxWidth: 300,
  '&:hover': {
    backgroundColor: PaymentMethodsConfig[method]?.buttonColor || '#1b5e20',
    opacity: 0.9,
  },
  marginTop: 8,
}));

const MethodLogoTop = styled('img')({
  maxWidth: 180,
  display: 'block',
  margin: '10px auto',
});

const FaturaProforma = ({ onClose, onPagamento, modo = 'dashboard' }) => {
  const { prestacaoId } = useParams();
  const [searchParams] = useSearchParams();
  const [prestacao, setPrestacao] = useState(null);
  const [transacoes, setTransacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { adicionarNotificacao } = useNotificacao();

  useEffect(() => {
    const carregarFatura = async () => {
      try {
        if (modo === 'publico') {
          const token = searchParams.get('token');
          const response = await pagamentoPublicoService.obterFaturaPublica(prestacaoId, token);
          setPrestacao(response.data);
        } else {
          const response = await pagamentoService.obterDadosFaturaProforma(prestacaoId);
          setPrestacao(response.data.prestacao);
          setTransacoes(response.data.transacoes || []);
        }
      } catch (error) {
        console.error('Erro ao carregar fatura:', error);
        setError(error.message || 'Erro ao carregar fatura.');
      } finally {
        setLoading(false);
      }
    };

    if (prestacaoId) {
      carregarFatura();
    } else {
      setError('ID da prestação não fornecido.');
      setLoading(false);
    }
  }, [prestacaoId, modo, searchParams]);

  const handleVisualizarFatura = async () => {
    try {
      if (modo === 'publico') {
        const token = searchParams.get('token');
        const response = await pagamentoPublicoService.obterFaturaPublica(prestacaoId, token, true);
        const pdfUrl = window.URL.createObjectURL(new Blob([response.data]));
        window.open(pdfUrl, '_blank');
      } else {
        const response = await pagamentoService.gerarFaturaProforma(prestacaoId);
        const pdfUrl = window.URL.createObjectURL(new Blob([response.data]));
        window.open(pdfUrl, '_blank');
      }
    } catch (error) {
      console.error('Erro ao visualizar fatura:', error);
      adicionarNotificacao('Erro ao carregar fatura', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Erro: {error}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={onClose}>
            Fechar
          </Button>
        </Box>
      </Box>
    );
  }

  if (!prestacao) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Prestação não encontrada.</Typography>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={onClose}>
            Fechar
          </Button>
        </Box>
      </Box>
    );
  }

  const valorTotal = parseFloat(prestacao.valor) + parseFloat(prestacao.valorMulta || 0);
  const iva = parseFloat(prestacao.valor) * 0.16;
  const totalComIva = parseFloat(prestacao.valor) + iva + parseFloat(prestacao.valorMulta || 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho adaptativo */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mb: 3,
          flexDirection: modo === 'publico' ? 'column' : 'row',
          alignItems: modo === 'publico' ? 'flex-start' : 'center'
        }}
      >
        <Box>
          {modo === 'publico' && (
            <Button
              href={process.env.FRONTEND_URL}
              startIcon={<ArrowBack />}
              sx={{ mb: 2 }}
            >
              Voltar ao Site
            </Button>
          )}
          <Typography variant="h5">
            Fatura #{prestacao.id}
          </Typography>
        </Box>
        
        {modo === 'dashboard' && (
          <Button onClick={onClose}>
            Fechar
          </Button>
        )}
      </Box>

      {/* Status e Avisos */}
      {prestacao.status === 'PAGO' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Esta fatura já foi paga.
        </Alert>
      )}

      {modo === 'publico' && prestacao.valorMulta > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle1">Atenção</Typography>
          Esta fatura inclui multa por atraso.
          <br />
          Valor original: {formatarMoeda(prestacao.valor)} MT
          <br />
          Multa: {formatarMoeda(prestacao.valorMulta)} MT
        </Alert>
      )}

      {/* Detalhes da Fatura */}
      <Grid container spacing={4}>
        {/* Informações do Cliente */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Facturado à:
            </Typography>
            <Typography>{prestacao.estudante.nome}</Typography>
            <Typography>{prestacao.estudante.endereco}</Typography>
            <Typography>NUIT: {prestacao.estudante.nuit || 'N/A'}</Typography>
          </Box>
        </Grid>

        {/* Informações da Instituição */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Emitido por:
            </Typography>
            <Typography>{prestacao.instituicao.nome}</Typography>
            <Typography>NUIT: {prestacao.instituicao.nuit}</Typography>
            {modo === 'publico' && (
              <>
                <Typography>Email: info@zenkroll.co.mz</Typography>
                <Typography>Tel: (+258) 84 635 1111 | 86 635 1111</Typography>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Tabela de Valores */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell align="right">Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Mensalidade {prestacao.mes}</TableCell>
              <TableCell align="right">{formatarMoeda(prestacao.valor)} MT</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">Sub Total</TableCell>
              <TableCell align="right">{formatarMoeda(prestacao.valor)} MT</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">IVA (16%)</TableCell>
              <TableCell align="right">{formatarMoeda(iva)} MT</TableCell>
            </TableRow>
            {prestacao.valorMulta > 0 && (
              <TableRow>
                <TableCell align="right">
                  <Typography color="error">Multa por Atraso</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography color="error">
                    {formatarMoeda(prestacao.valorMulta)} MT
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell align="right">
                <Typography variant="subtitle1">Total</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle1">
                  {formatarMoeda(totalComIva)} MT
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Ações */}
      {prestacao.status !== 'PAGO' && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: modo === 'publico' ? 'center' : 'flex-end',
          mt: 3 
        }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onPagamento(prestacao)}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              'Realizar Pagamento'
            )}
          </Button>
        </Box>
      )}

      {/* Seção de Ajuda para Modo Público */}
      {modo === 'publico' && (
        <Box mt={6} pt={3} borderTop={1} borderColor="divider">
          <Typography variant="h6" gutterBottom>
            Precisa de Ajuda?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Se você tiver alguma dúvida ou encontrar problemas durante o pagamento,
            entre em contato com nosso suporte:
          </Typography>
          <Typography variant="body2" paragraph>
            • Email: suporte@zenkroll.co.mz<br />
            • Telefone: (+258) 84 635 1111 | 86 635 1111<br />
            • Horário de Atendimento: Segunda a Sexta, 8h às 17h
          </Typography>
          <Button 
            variant="outlined"
            href={`${process.env.FRONTEND_URL}/dashboard`}
            sx={{ mt: 2 }}
          >
            Acessar Dashboard
          </Button>
        </Box>
      )}
    </Box>
  );
};

FaturaProforma.propTypes = {
  onClose: PropTypes.func.isRequired,
  onPagamento: PropTypes.func.isRequired,
  modo: PropTypes.oneOf(['dashboard', 'publico'])
};

FaturaProforma.defaultProps = {
  modo: 'dashboard'
};

export default FaturaProforma;
